import { useCallback, useEffect, useRef, useState } from "react";
import { getOpsDailyChecklistConfig, patchOpsDailyChecklistConfig, postChecklistBroadcastNudge } from "@/lib/opsPaperworkClient";
import {
  ID_RE,
  PLATFORM_KEY_RE,
  cloneDefaultManualRows,
  cloneDefaultPlatforms,
  manualRowFromConfig,
  parseDayList,
  parseMonthDaysStr,
  platformRowsWithStableIdsFromAds,
  scheduleFromRow,
  type ManualRow,
  type PlatformRow,
} from "./salesChecklistOrgEditorTypes";
import { DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY } from "@/lib/lead/extraction/leadDefaultFollowUpPolicy";

export function useSalesChecklistOrgEditorModel(_organizationId: string) {
  /** Preserve Ads-assistant-only keys on `ads` when saving from Ops / Location Tools. */
  const prevAdsSnapshotRef = useRef<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [adsEnabled, setAdsEnabled] = useState(true);
  const [adsUseInOrgActivity, setAdsUseInOrgActivity] = useState(true);
  const [platformRows, setPlatformRows] = useState<PlatformRow[]>(cloneDefaultPlatforms());
  const [topicKeywords, setTopicKeywords] = useState("");
  const [leadHygieneDays, setLeadHygieneDays] = useState("");
  const [leadHygieneCadence, setLeadHygieneCadence] = useState("3, 7, 14");
  const [ackBlockingEnabled, setAckBlockingEnabled] = useState(false);
  const [floodGuardrailsEnabled, setFloodGuardrailsEnabled] = useState(true);
  const [manualRows, setManualRows] = useState<ManualRow[]>(cloneDefaultManualRows());
  const [managerNudgePersist, setManagerNudgePersist] = useState<{ title: string; body: string; sent_at: string } | null>(null);
  const [nudgeTitle, setNudgeTitle] = useState("");
  const [nudgeBody, setNudgeBody] = useState("");
  const [broadcastBusy, setBroadcastBusy] = useState(false);

  const [leadDefaultFirstFollowUpEnabled, setLeadDefaultFirstFollowUpEnabled] = useState(false);
  const [leadDefaultHotAction, setLeadDefaultHotAction] = useState(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_hot);
  const [leadDefaultWarmAction, setLeadDefaultWarmAction] = useState(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_warm);
  const [leadDefaultColdAction, setLeadDefaultColdAction] = useState(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_cold);
  const [leadDefaultDueHoursHot, setLeadDefaultDueHoursHot] = useState(String(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_hot));
  const [leadDefaultDueHoursWarm, setLeadDefaultDueHoursWarm] = useState(String(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_warm));
  const [leadDefaultDueHoursCold, setLeadDefaultDueHoursCold] = useState(String(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_cold));

  const resetToAppDefaults = useCallback(() => {
    prevAdsSnapshotRef.current = {};
    setAdsEnabled(true); setAdsUseInOrgActivity(true); setPlatformRows(cloneDefaultPlatforms()); setTopicKeywords("");
    setLeadHygieneDays(""); setLeadHygieneCadence("3, 7, 14"); setAckBlockingEnabled(false);
    setManualRows(cloneDefaultManualRows()); setManagerNudgePersist(null); setNudgeTitle(""); setNudgeBody("");
    setLeadDefaultFirstFollowUpEnabled(false);
    setLeadDefaultHotAction(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_hot);
    setLeadDefaultWarmAction(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_warm);
    setLeadDefaultColdAction(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_cold);
    setLeadDefaultDueHoursHot(String(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_hot));
    setLeadDefaultDueHoursWarm(String(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_warm));
    setLeadDefaultDueHoursCold(String(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_cold));
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const r = await getOpsDailyChecklistConfig();
      if (!r.ok) throw new Error(r.error);
      const payload = r.data as {
        config?: Record<string, unknown> | null;
        ops_config?: Record<string, unknown> | null;
        legacy_org_config?: Record<string, unknown> | null;
      };
      const raw = payload.config ?? payload.ops_config ?? payload.legacy_org_config ?? null;
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        prevAdsSnapshotRef.current = {};
        resetToAppDefaults();
        return;
      }
      const c = raw;
      const mnRaw = c.manager_nudge as Record<string, unknown> | null | undefined;
      if (mnRaw && typeof mnRaw.title === "string" && typeof mnRaw.sent_at === "string") {
        const mn = { title: mnRaw.title, body: typeof mnRaw.body === "string" ? mnRaw.body : "", sent_at: mnRaw.sent_at };
        setManagerNudgePersist(mn); setNudgeTitle(mn.title); setNudgeBody(mn.body);
      } else { setManagerNudgePersist(null); setNudgeTitle(""); setNudgeBody(""); }
      const ads = c.ads as Record<string, unknown> | null | undefined;
      prevAdsSnapshotRef.current =
        ads && typeof ads === "object" && !Array.isArray(ads) ? { ...ads } : {};
      setAdsEnabled(ads?.enabled !== false);
      setAdsUseInOrgActivity(ads?.use_in_org_activity !== false);
      setPlatformRows(platformRowsWithStableIdsFromAds(ads ?? null));
      setTopicKeywords(Array.isArray(ads?.topic_keywords) ? (ads!.topic_keywords as string[]).join(", ") : "");
      const hyRaw = c.lead_followup_hygiene_days;
      setLeadHygieneDays(typeof hyRaw === "number" && hyRaw >= 1 && hyRaw <= 365 ? String(hyRaw) : "");
      const hyCadence = Array.isArray(c.lead_followup_hygiene_cadence_days) ? (c.lead_followup_hygiene_cadence_days as number[]) : [3, 7, 14];
      setLeadHygieneCadence(hyCadence.filter((n) => typeof n === "number" && n > 0 && n <= 365).join(", "));
      const notif = c.notifications && typeof c.notifications === "object" ? (c.notifications as Record<string, unknown>) : {};
      setAckBlockingEnabled(notif.ack_blocking_enabled === true);
      setFloodGuardrailsEnabled(notif.flood_guardrails_enabled !== false);
      const ldf = c.lead_default_first_follow_up;
      if (ldf && typeof ldf === "object" && !Array.isArray(ldf)) {
        const b = ldf as Record<string, unknown>;
        setLeadDefaultFirstFollowUpEnabled(b.enabled === true);
        setLeadDefaultHotAction(typeof b.next_action_hot === "string" && b.next_action_hot.trim() ? b.next_action_hot : DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_hot);
        setLeadDefaultWarmAction(typeof b.next_action_warm === "string" && b.next_action_warm.trim() ? b.next_action_warm : DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_warm);
        setLeadDefaultColdAction(typeof b.next_action_cold === "string" && b.next_action_cold.trim() ? b.next_action_cold : DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_cold);
        const dh = (v: unknown, fb: number) => {
          const n = typeof v === "number" ? v : parseInt(String(v ?? ""), 10);
          return Number.isFinite(n) && n >= 1 && n <= 720 ? String(Math.round(n)) : String(fb);
        };
        setLeadDefaultDueHoursHot(dh(b.due_hours_hot, DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_hot));
        setLeadDefaultDueHoursWarm(dh(b.due_hours_warm, DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_warm));
        setLeadDefaultDueHoursCold(dh(b.due_hours_cold, DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_cold));
      } else {
        setLeadDefaultFirstFollowUpEnabled(false);
        setLeadDefaultHotAction(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_hot);
        setLeadDefaultWarmAction(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_warm);
        setLeadDefaultColdAction(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_cold);
        setLeadDefaultDueHoursHot(String(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_hot));
        setLeadDefaultDueHoursWarm(String(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_warm));
        setLeadDefaultDueHoursCold(String(DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_cold));
      }
      const manualArr = Array.isArray(c.manual) ? (c.manual as Record<string, unknown>[]) : [];
      if (manualArr.length > 0) {
        const rows = manualArr.map(manualRowFromConfig).filter((x): x is ManualRow => x != null);
        setManualRows(rows.length ? rows : cloneDefaultManualRows());
      } else setManualRows(cloneDefaultManualRows());
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMsg({ text: err?.message || String(e), ok: false });
    } finally { setLoading(false); }
  }, [resetToAppDefaults]);
  useEffect(() => { void load(); }, [load]);

  const save = useCallback(async (managerNudgeOverride?: { title: string; body: string; sent_at: string } | null) => {
    setLoading(true); setMsg(null);
    try {
      for (const row of manualRows) {
        if (!ID_RE.test(row.id)) throw new Error(`Invalid id "${row.id}" — lowercase, numbers, underscores (max 63).`);
        if (!row.label.trim()) throw new Error(`Every row needs a label (id: ${row.id}).`);
        if ((row.schedule_type === "monthly" || row.schedule_type === "quarterly") && parseMonthDaysStr(row.month_days).length === 0) throw new Error(`"${row.label}": add at least one calendar day (1–31) for ${row.schedule_type} schedule.`);
        if (row.schedule_type === "weekly" && !row.weekdays.some(Boolean)) throw new Error(`"${row.label}": pick at least one weekday.`);
      }
      const hyTrim = leadHygieneDays.trim();
      if (hyTrim) { const n = parseInt(hyTrim, 10); if (!Number.isFinite(n) || n < 1 || n > 365) throw new Error("Stale-lead hygiene window must be between 1 and 365 days, or leave blank to disable."); }
      if (adsEnabled && platformRows.length === 0) throw new Error("Add at least one ad platform row, or turn off ad logging.");
      for (const p of platformRows) { if (!PLATFORM_KEY_RE.test(p.key)) throw new Error(`Invalid platform key "${p.key}".`); if (!p.label.trim()) throw new Error(`Platform "${p.key}" needs a label.`); }
      const keywords = topicKeywords.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean).slice(0, 24);
      const platform_entries = platformRows.map(({ key, label, daily_slots }) => ({
        key: key.trim().toLowerCase(),
        label: label.trim(),
        daily_slots: Math.min(20, Math.max(1, daily_slots)),
      }));
      const manual = manualRows.map((row) => ({ id: row.id.trim(), label: row.label.trim(), requires_confirm: row.requires_confirm, schedule: scheduleFromRow(row), ...(row.description.trim() ? { description: row.description.trim() } : {}) }));
      const parseH = (s: string, fb: number) => {
        const n = parseInt(s.trim(), 10);
        return Number.isFinite(n) && n >= 1 && n <= 720 ? n : fb;
      };
      const lead_default_first_follow_up = {
        enabled: leadDefaultFirstFollowUpEnabled,
        next_action_hot: leadDefaultHotAction.trim() || DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_hot,
        next_action_warm: leadDefaultWarmAction.trim() || DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_warm,
        next_action_cold: leadDefaultColdAction.trim() || DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.next_action_cold,
        due_hours_hot: parseH(leadDefaultDueHoursHot, DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_hot),
        due_hours_warm: parseH(leadDefaultDueHoursWarm, DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_warm),
        due_hours_cold: parseH(leadDefaultDueHoursCold, DEFAULT_LEAD_DEFAULT_FOLLOW_UP_POLICY.due_hours_cold),
      };
      const prevAds = prevAdsSnapshotRef.current;
      const adsMerged = adsEnabled
        ? {
            ...prevAds,
            enabled: true,
            platform_entries,
            use_in_org_activity: adsUseInOrgActivity,
            ...(keywords.length ? { topic_keywords: keywords } : {}),
          }
        : { ...prevAds, enabled: false, use_in_org_activity: adsUseInOrgActivity };

      const config: Record<string, unknown> = {
        version: 2,
        ads: adsMerged,
        manual,
        lead_followup_hygiene_days: hyTrim ? parseInt(hyTrim, 10) : null,
        lead_followup_hygiene_cadence_days: parseDayList(leadHygieneCadence),
        notifications: {
          ack_blocking_enabled: ackBlockingEnabled,
          flood_guardrails_enabled: floodGuardrailsEnabled,
        },
        lead_default_first_follow_up,
      };
      const mn = managerNudgeOverride !== undefined ? managerNudgeOverride : managerNudgePersist;
      if (mn) config.manager_nudge = { title: mn.title.slice(0, 500), body: mn.body.slice(0, 8000), sent_at: mn.sent_at };
      const r = await patchOpsDailyChecklistConfig(config);
      if (!r.ok) throw new Error(r.error);
      prevAdsSnapshotRef.current =
        adsMerged && typeof adsMerged === "object" && !Array.isArray(adsMerged)
          ? { ...(adsMerged as Record<string, unknown>) }
          : {};
      if (managerNudgeOverride) setManagerNudgePersist(managerNudgeOverride);
      setMsg({ text: "Saved. Reps see updates after refresh.", ok: true });
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMsg({ text: err?.message || String(e), ok: false });
    } finally { setLoading(false); }
  }, [
    ackBlockingEnabled,
    floodGuardrailsEnabled,
    adsEnabled,
    adsUseInOrgActivity,
    leadDefaultColdAction,
    leadDefaultDueHoursCold,
    leadDefaultDueHoursHot,
    leadDefaultDueHoursWarm,
    leadDefaultFirstFollowUpEnabled,
    leadDefaultHotAction,
    leadDefaultWarmAction,
    leadHygieneCadence,
    leadHygieneDays,
    managerNudgePersist,
    manualRows,
    platformRows,
    topicKeywords,
  ]);

  const sendOrgNudgeBroadcast = useCallback(async () => {
    const title = nudgeTitle.trim();
    if (!title) { setMsg({ text: "Add a short title for the org nudge.", ok: false }); return; }
    setBroadcastBusy(true); setMsg(null);
    try {
      const r = await postChecklistBroadcastNudge({ title, body: nudgeBody.trim() });
      if (!r.ok) throw new Error(r.error);
      const data = r.data as { recipient_count?: number; sent_at?: string };
      const mn = { title: title.slice(0, 500), body: nudgeBody.trim().slice(0, 8000), sent_at: typeof data.sent_at === "string" ? data.sent_at : new Date().toISOString() };
      await save(mn);
      setMsg({ text: `Sent to ${typeof data.recipient_count === "number" ? data.recipient_count : "all"} org member(s). Header bell + Leads checklist updated.`, ok: true });
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMsg({ text: err?.message || String(e), ok: false });
    } finally { setBroadcastBusy(false); }
  }, [nudgeBody, nudgeTitle, save]);

  const clearConfig = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const r = await patchOpsDailyChecklistConfig(null);
      if (!r.ok) throw new Error(r.error);
      resetToAppDefaults();
      setMsg({ text: "Cleared Ops checklist override — legacy org config (if any) applies until you save again.", ok: true });
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMsg({ text: err?.message || String(e), ok: false });
    } finally { setLoading(false); }
  }, [resetToAppDefaults]);

  return {
    loading, msg, adsEnabled, adsUseInOrgActivity, platformRows, topicKeywords, leadHygieneDays, leadHygieneCadence, ackBlockingEnabled, floodGuardrailsEnabled,
    manualRows, managerNudgePersist, nudgeTitle, nudgeBody, broadcastBusy,
    leadDefaultFirstFollowUpEnabled,
    leadDefaultHotAction,
    leadDefaultWarmAction,
    leadDefaultColdAction,
    leadDefaultDueHoursHot,
    leadDefaultDueHoursWarm,
    leadDefaultDueHoursCold,
    setAdsEnabled, setAdsUseInOrgActivity, setPlatformRows, setTopicKeywords, setLeadHygieneDays, setLeadHygieneCadence, setAckBlockingEnabled, setFloodGuardrailsEnabled,
    setManualRows, setNudgeTitle, setNudgeBody,
    setLeadDefaultFirstFollowUpEnabled,
    setLeadDefaultHotAction,
    setLeadDefaultWarmAction,
    setLeadDefaultColdAction,
    setLeadDefaultDueHoursHot,
    setLeadDefaultDueHoursWarm,
    setLeadDefaultDueHoursCold,
    save, resetToAppDefaults, clearConfig, sendOrgNudgeBroadcast,
  };
}

